import { useAtomValue } from 'jotai';
import React, { useCallback, useState } from 'react';
import styles from '../stripes.module.scss';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';

import {
  FocusableComponentLayout,
  FocusContext,
  FocusDetails,
  FocusHandler,
  doesFocusableExist,
  getCurrentFocusKey,
  setFocus,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import { focusKeyPrevAtom, isHeroRenderedAtom } from '@smart-tv/atoms';
import ContentSlider from '@smart-tv/components/content-slider/content-slider';
import ContentSliderItem from '@smart-tv/components/content-slider/content-slider-item';
import { rootStore as stripesRootStore } from '@smart-tv/components/stripes/_stores/store-root';
import {
  ActionableContentConsumer,
  ActionableContentProvider
} from '@smart-tv/contexts/actionable-content-context';
import useUiElementContent from '@smart-tv/hooks/use-ui-element-content';
import {
  ProviderContentProps,
  UIElementContentSource
} from '@smart-tv/hooks/use-ui-element-content/use-ui-element-content';
import { ContentItem, UIElement, UIElementStyle } from '@smart-tv/typings/content';

import {
  LANDSCAPE_SCROLL_WIDTH,
  LIVE_TV_STRIPE_ID,
  MOVIE_STRIPE_ID,
  PORTRAIT_SCROLL_WIDTH,
  WIDE_LANDSCAPE_SCROLL_WIDTH
} from '../_utils/types';
import StripesHero from './stripes-hero';
import StripesShowAll from './stripes-show-all';
import { UIElementExtended } from '../_stores/store-stripes-base';

interface Props {
  uiElement: UIElementExtended;
  onFocus: (layout: FocusableComponentLayout, props: object, details: FocusDetails) => void;
  handleShowAllEnterPress: (uiElement: UIElement) => void;
  uIElementIndex: number;
  focusFirstElement?: boolean;
  contentSource: UIElementContentSource;
  providerContentProps?: ProviderContentProps;
  style?: UIElementStyle;
  universeTitle?: string;
  displayHero: boolean;
  fetchAll: boolean;
  readyForFocus?: boolean;
  setReadyForFocus?: React.Dispatch<React.SetStateAction<boolean>>;
}

const StripesUIElement: React.FC<Props> = (props: Props) => {
  const {
    contentSource,
    providerContentProps,
    focusFirstElement,
    uIElementIndex,
    uiElement,
    style,
    handleShowAllEnterPress,
    universeTitle,
    displayHero = false,
    fetchAll,
    onFocus,
    readyForFocus,
    setReadyForFocus
  } = props;
  const { uiElementContent, handleFavoritesUpdate } = useUiElementContent(
    uiElement.id,
    contentSource,
    fetchAll,
    uiElement.disablePagination,
    providerContentProps,
    style,
    undefined,
    uiElement.content
  );

  React.useEffect(() => {
    if (handleFavoritesUpdate) {
      stripesRootStore.stripesStore.registerStripeUpdateCallback(
        uiElement.id,
        handleFavoritesUpdate
      );
    }
    return () => {
      if (handleFavoritesUpdate) {
        stripesRootStore.stripesStore.unregisterStripeUpdateCallback(
          uiElement.id,
          handleFavoritesUpdate
        );
      }
    };
  }, [uiElement.id, handleFavoritesUpdate]);

  const { focusKey, ref } = useFocusable({
    onFocus,
    focusable: Boolean(uiElementContent?.items?.length)
  });

  const [focusedItem, setFocusedItem] = React.useState<ContentItem | undefined>(undefined);

  /*
    if the target is a browser, we want to show all the items in the stripe, 
    otherwise we want to limit the number of items shown in the stripe to 8. 

    - MRA
  */
  const stripeLength = IS_BROWSER
    ? Number.MAX_SAFE_INTEGER
    : uiElementContent?.showAllElementCell.show
    ? uiElementContent?.showAllElementCell.position
    : 8;

  const [animating, setAnimating] = React.useState(false);

  const scrollingRef = React.useRef<HTMLDivElement>(null);

  const focusKeyPrev = useAtomValue(focusKeyPrevAtom);

  const [hasMadeInitialFocus, setHasMadeInitialFocus] = useState(false);

  const isHeroRendered = useAtomValue(isHeroRenderedAtom);

  const getSecondXScrollPosition = React.useCallback(() => {
    switch (uiElement.id) {
      case MOVIE_STRIPE_ID:
        return PORTRAIT_SCROLL_WIDTH;

      case LIVE_TV_STRIPE_ID:
        return WIDE_LANDSCAPE_SCROLL_WIDTH;

      default:
        return LANDSCAPE_SCROLL_WIDTH;
    }
  }, [uiElement.id]);

  const onContentItemActive = useCallback((item: ContentItem) => {
    setHasMadeInitialFocus(true);
    setFocusedItem(item);
  }, []);

  const onContentItemFocus = useCallback(
    (item: ContentItem): FocusHandler =>
      ({ node }) => {
        onContentItemActive(item);

        if (scrollingRef.current) {
          // Use the node's own offsetLeft (stable) rather than the scroll-relative x from the layout,
          // which would compound drift across successive focus moves.
          scrollingRef.current.scrollTo({
            left: node.offsetLeft - getSecondXScrollPosition()
          });
        }
      },
    [getSecondXScrollPosition, onContentItemActive]
  );

  const isFocused = useCallback(
    (itemKey: string, index: number) => {
      if (
        !hasMadeInitialFocus &&
        index === 0 &&
        focusKeyPrev?.focusKeyPrev?.endsWith('0') &&
        focusKeyPrev?.isLiveTV
      ) {
        return focusKeyPrev.focusKeyPrev === itemKey;
      }

      return !!(!hasMadeInitialFocus && focusFirstElement && index === 0);
    },
    [focusFirstElement, focusKeyPrev, hasMadeInitialFocus]
  );

  React.useEffect(() => {
    const firstItemFocusKey = `0-${uIElementIndex}`;

    if (readyForFocus && !getCurrentFocusKey() && doesFocusableExist(firstItemFocusKey)) {
      firstItemFocusKey && setFocus(firstItemFocusKey);
    }
  }, [readyForFocus, uIElementIndex]);

  if (!uiElementContent) {
    return <div className={styles['ui-elements']} ref={ref} />;
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <div className={styles['ui-elements']} ref={ref}>
        <ActionableContentProvider items={uiElementContent.items}>
          <ActionableContentConsumer>
            {({ actionableItems }) => {
              return actionableItems?.length ? (
                <ContentSlider
                  hero={
                    displayHero && uIElementIndex === 0 ? (
                      <StripesHero
                        contentItem={focusedItem ?? uiElementContent.items[0]}
                        universeTitle={universeTitle}
                      />
                    ) : undefined
                  }
                  title={uiElement.title}
                  isHeroRendered={isHeroRendered}
                  scrollingRef={scrollingRef}
                  onShowAll={
                    uiElementContent.showAllElementCell.show
                      ? () => handleShowAllEnterPress(uiElement)
                      : undefined
                  }
                >
                  {actionableItems?.map((item, index) => {
                    const focusKey = `${index}-${uIElementIndex}`;
                    return (
                      index < stripeLength && (
                        <ContentSliderItem
                          key={focusKey}
                          contentItem={item}
                          isUnavailable={
                            item.providerCount !== undefined && item.providerCount <= 0
                          }
                          showProgress={Boolean(uiElementContent.properties.showProgress)}
                          showStartTime={Boolean(uiElementContent.properties.showStartTime)}
                          showContentTitle={Boolean(uiElementContent.properties.showContentTitle)}
                          onFocus={onContentItemFocus(item)}
                          onMouseEnter={() => onContentItemActive(item)}
                          contentSliderIndex={index}
                          liveTV={uiElement.id === LIVE_TV_STRIPE_ID}
                          animating={animating}
                          setAnimating={setAnimating}
                          isFocused={isFocused(focusKey, index)}
                          focusKey={focusKey}
                          stripePrimaryAction={uiElementContent.primaryAction}
                          handleFavoritesUpdate={handleFavoritesUpdate}
                          // Set ready for focus on mount to ensure that the parent knows the page is ready for focus. "setReadyForFocus" is only defined for a single item in a single stripe, so we only call it for the last item in the stripe.
                          setReadyForFocus={
                            index === Math.min(7, actionableItems.length - 1)
                              ? setReadyForFocus
                              : undefined
                          }
                        />
                      )
                    );
                  })}
                  {uiElementContent.showAllElementCell.show && !IS_BROWSER && (
                    /*
                      The show all button is only rendered if the stripe has more than 8 items.
                      The show all button is not rendered on browser.
                      - MRA
                    */
                    <StripesShowAll
                      show={uiElementContent.showAllElementCell.show}
                      handleShowAllEnterPress={() => handleShowAllEnterPress(uiElement)}
                      uiElementStyleName={uiElement.style}
                      focusKey={`showAll-${uIElementIndex}`}
                    />
                  )}
                </ContentSlider>
              ) : null;
            }}
          </ActionableContentConsumer>
        </ActionableContentProvider>
      </div>
    </FocusContext.Provider>
  );
};

export default React.memo(StripesUIElement);
